import jStat from 'jstat';
import type { SingleArrayParams, SampleParams, BoxPlotStats } from './types';
import { calculateIQR as calculateIQRDetailed } from './quartiles';

export interface ZScoreParams {
  value: number;
  data: readonly number[];
}

/**
 * Calculates the quartiles (Q1, Q2/median, Q3) of a dataset
 * @param data Array of numbers
 * @returns Object containing min, Q1, median, Q3, and max values
 */
export function calculateBoxPlotStats(data: readonly number[]): BoxPlotStats {
  if (data.length === 0) {
    return {
      min: 0,
      q1: 0,
      median: 0,
      q3: 0,
      max: 0
    };
  }

  if (data.length === 1) {
    const value = data[0] ?? 0;
    return {
      min: value,
      q1: value,
      median: value,
      q3: value,
      max: value
    };
  }

  const quartiles = jStat.quartiles(data);
  
  return {
    min: jStat.min(data),
    q1: quartiles[0],
    median: quartiles[1],
    q3: quartiles[2],
    max: jStat.max(data)
  };
}

/**
 * Calculates the interquartile range (IQR) of a dataset
 * @param params Object containing the data array
 * @returns The interquartile range
 */
export function calculateIQR({ data }: SingleArrayParams): number {
  if (data.length <= 1) return 0;
  const result = calculateIQRDetailed(data);
  return result.iqr;
}

/**
 * Identifies outliers in a dataset using the 1.5 * IQR rule
 * @param params Object containing the data array
 * @returns Array of outliers
 */
export function findOutliers({ data }: SingleArrayParams): number[] {
  if (data.length <= 1) return [];
  const result = calculateIQRDetailed(data);
  return result.outliers;
}

/**
 * Calculates the arithmetic mean of a dataset
 * @param params Object containing the data array
 * @returns The arithmetic mean
 */
export function calculateMean({ data }: SingleArrayParams): number {
  if (data.length === 0) return 0;
  return jStat.mean(data);
}

/**
 * Calculates the median (middle value) of a dataset
 * @param params Object containing the data array
 * @returns The median value
 */
export function calculateMedian({ data }: SingleArrayParams): number {
  if (data.length === 0) return 0;
  return jStat.median(data);
}

/**
 * Calculates the mode (most frequent value) of a dataset
 * @param params Object containing the data array
 * @returns The mode value
 */
export function calculateMode({ data }: SingleArrayParams): number {
  if (data.length === 0) return 0;
  return jStat.mode(data);
}

/**
 * Calculates the range (difference between max and min) of a dataset
 * @param params Object containing the data array
 * @returns The range value
 */
export function calculateRange({ data }: SingleArrayParams): number {
  if (data.length === 0) return 0;
  return jStat.range(data);
}

/**
 * Calculates the variance of a dataset
 * @param params Object containing the data array and optional sample flag
 * @returns The variance
 */
export function calculateVariance({ data, sample = true }: SampleParams): number {
  if (data.length === 0) return 0;
  return jStat.variance(data, sample);
}

/**
 * Calculates the standard deviation of a dataset
 * @param params Object containing the data array and optional sample flag
 * @returns The standard deviation
 */
export function calculateStandardDeviation({ data, sample = true }: SampleParams): number {
  if (data.length === 0) return 0;
  return jStat.stdev(data, sample);
}

/**
 * Calculates the coefficient of variation (CV) of a dataset
 * CV = (standard deviation / mean) * 100
 * @param params Object containing the data array
 * @returns The coefficient of variation as a percentage
 */
export function calculateCoefficientOfVariation({ data }: SingleArrayParams): number {
  if (data.length === 0) return 0;
  return jStat.coeffvar(data) * 100;
}

/**
 * Calculates the z-score for a value
 * Z-score represents how many standard deviations away from the mean a value is
 * @param params Object containing the value and data array
 * @returns The z-score
 */
export function calculateZScore({ value, data }: ZScoreParams): number {
  if (data.length === 0) return 0;
  const mean = jStat.mean(data);
  const sd = jStat.stdev(data, true);
  return jStat.zscore(value, mean, sd);
}

/**
 * Calculates the kurtosis of a dataset
 * Kurtosis measures the "tailedness" of a probability distribution
 * @param params Object containing the data array
 * @returns The kurtosis value
 */
export function calculateKurtosis({ data }: SingleArrayParams): number {
  if (data.length === 0) return 0;
  return jStat.kurtosis(data);
}

/**
 * Calculates the minimum value in a dataset
 * @param params Object containing the data array
 * @returns The minimum value
 */
export function calculateMin({ data }: SingleArrayParams): number {
  if (data.length === 0) return 0;
  return jStat.min(data);
}

/**
 * Calculates the maximum value in a dataset
 * @param params Object containing the data array
 * @returns The maximum value
 */
export function calculateMax({ data }: SingleArrayParams): number {
  if (data.length === 0) return 0;
  return jStat.max(data);
} 